import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  CustomSelect,
  ErrorInfo,
  SectionHeader,
  TextField,
  Typography, WordCountArea,
} from 'web-library';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Theme as AugmentedTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import {
  useGetWithdrawalDropDown
} from '../../../../api/candidate/exam/@candidate/queries-services/queries';
import { withdrawalTypeData } from '../../../admin/logic/dropDownOption';
import RouteKey from '../../../../routes/key';
import {
  useGetWithdrawalSelectedReason,
  usePutWithdrawalReason,
} from '../../../../api/candidate/exam/@coordinator/queries-services/queries';
import {
  ExamType,
  WithdrawalReason as WithdrawalReasonModel,
} from '../../../../api/candidate/exam/model';
import DraftTimeStamp from '../../../../components/DraftTimeStamp';

interface WithdrawalReasonProps {
  examTypeCode: ExamType;
  lastUpdatedTime?: string;
}

const { withdrawal } = RouteKey.child;
const { selectCandidates, withdrawal: withdrawalRoute, instruction } = RouteKey.withdrawal.child;

const useStyles = makeStyles()((theme) =>
({
  header: {
    marginBottom: theme.spacing(3),
  },
  select: {
    marginBottom: theme.spacing(6),
  },
  inputLabel: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(1),
    color: theme.color.text.textHeader,
  },
  buttonContainer: {
    marginTop: theme.spacing(4),
    display: 'flex',
  },
  button: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
  },
}));

const MAX_LENGTH = 200;

const WithdrawalReason = ({ examTypeCode, lastUpdatedTime }: WithdrawalReasonProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();
  const { uuid } = useParams<{ uuid: string }>();

  const [reason, setReason] = useState<WithdrawalReasonModel>();
  const [isFetchedDraft, setIsFetchedDraft] = useState(false);
  const [specifiedReason, setSpecifiedReason] = useState<string>();
  const [charactersRemaining, setCharactersRemaining] = useState({
    num: MAX_LENGTH,
    s: 's',
  });
  const [errorMsg, setErrorMsg] = useState<string>('');

  const { data: dropdownData } = useGetWithdrawalDropDown();

  useGetWithdrawalSelectedReason(uuid, (res) => {
    setReason(res.reason);
    if (res.specifiedReason) {
      setSpecifiedReason(res.specifiedReason);
    }
    setIsFetchedDraft(true);
  });

  const putWithdrawalReason = usePutWithdrawalReason(
    () => {
      history.push(`/${withdrawal}/${withdrawalRoute.route}/${uuid}/${selectCandidates.route}`, {
        withdrawalReason: reason,
      });
    },
    (err) => setErrorMsg(err?.response?.data.message!)
  );

  const handleReasonChange = (value: string) => {
    if (dropdownData) {
      setReason(dropdownData.find((item) => item.reasonDescription === value));
    }
    setSpecifiedReason(undefined);
  };

  const handleSpecifiedReasonChange = (target: any) => {
    setSpecifiedReason(target.value);
    const num = target.maxLength - target.value.length;
    setCharactersRemaining({ num, s: num > 1 ? 's' : '' });
  };

  const handleBack = () =>
    history.push(`/${withdrawal}/${withdrawalRoute.route}/${instruction.route}/${examTypeCode}`);

  const handleConfirm = () => {
    if (reason) {
      putWithdrawalReason.mutate({
        payload: {
          uuid,
          reason,
          specifiedReason,
        },
      });
    }
  };

  return (
    <ContentTemplate type="full">
      <SectionHeader title={t('withdrawal->>Coordinator.Reason.SectionHeader')} />

      <Typography className={classes.header} variant="h3">
        {t('withdrawal->>Coordinator.Reason.SelectReason')}
      </Typography>

      {dropdownData && isFetchedDraft && (
        <CustomSelect
          id="input-select-reason"
          placeholder="Select Reason"
          displayEmpty
          value={reason?.reasonDescription}
          onChange={({ target: { value } }) => handleReasonChange(value as string)}
          menuItems={withdrawalTypeData(dropdownData)}
        />
      )}

      {reason?.additionalTextNotice && (
        <>
          <Typography className={classes.inputLabel} variant="h5">
            {reason?.additionalTextNotice}
          </Typography>
          <WordCountArea
              name="inputReason-withdrawalReason"
              placeholder={t('admin->>UserGroups.EnterRemarks')}
              maxLength={MAX_LENGTH}
              minRows={5}
              onChange={({ target }) => handleSpecifiedReasonChange(target)}
              value={specifiedReason}
          />
        </>
      )}

      <ErrorInfo label={errorMsg} />

      <Box className={classes.buttonContainer}>
        <Button
          className={classes.button}
          type="button"
          color="secondary"
          variant="outlined"
          onClick={handleBack}
        >
          {t('withdrawal->>Coordinator.Reason.Back')}
        </Button>

        <ApiButton
          id="button-confirm-reason"
          className={classes.button}
          variant="contained"
          status={putWithdrawalReason.status}
          disabled={!reason || (!!reason?.additionalTextNotice && !specifiedReason)}
          onClick={handleConfirm}
        >
          {t('withdrawal->>Coordinator.Reason.ConfirmReason')}
        </ApiButton>
      </Box>
      {lastUpdatedTime && <DraftTimeStamp savedAt={lastUpdatedTime} />}
    </ContentTemplate>
  );
};

export default WithdrawalReason;
